import { useParams, Link, Navigate } from "react-router-dom";
import { Check, ArrowUpRight, ArrowLeft } from "lucide-react";
import SEO from "@/components/SEO";
import PageHero from "@/components/PageHero";
import CTABand from "@/components/CTABand";
import { Container, Reveal, ImageReveal } from "@/components/Reveal";
import { INDUSTRIES, SERVICES } from "@/data/content";

export default function IndustryDetail() {
  const { slug } = useParams();
  const ind = INDUSTRIES.find((i) => i.slug === slug);
  if (!ind) return <Navigate to="/industries" replace />;

  return (
    <>
      <SEO
        title={`${ind.name} Energy Solutions`}
        description={ind.summary}
        path={`/industries/${slug}`}
      />
      <PageHero
        overline="Industries"
        titleLines={[ind.name]}
        subtitle={ind.summary}
        crumbs={[
          { label: "Industries", path: "/industries" },
          { label: ind.name, path: `/industries/${slug}` },
        ]}
        image={ind.image}
      />

      <section className="bg-white py-24 md:py-28">
        <Container>
          <Link
            to="/industries"
            className="mb-12 inline-flex items-center gap-2 text-sm font-medium text-slatey transition-colors hover:text-navy"
          >
            <ArrowLeft className="h-4 w-4" /> All industries
          </Link>

          <div className="grid grid-cols-1 gap-12 lg:grid-cols-2 lg:gap-16">
            <div>
              <p className="overline text-electric">How we help</p>
              <h2 className="mt-4 font-heading text-3xl font-semibold tracking-tight text-navy md:text-4xl">
                Energy procurement for {ind.name.toLowerCase()}.
              </h2>
              <p className="mt-6 text-lg leading-relaxed text-slatey">
                {ind.summary} We align contract structure, timing, and risk strategy
                to the operational realities of your sector — so energy supports the
                business rather than surprising it.
              </p>
              <ul className="mt-8 space-y-4">
                {ind.points.map((p, i) => (
                  <Reveal key={i} delay={i * 0.06}>
                    <li className="flex items-start gap-4">
                      <span className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-energy/12 text-energy">
                        <Check className="h-4 w-4" />
                      </span>
                      <span className="text-navy/80">{p}</span>
                    </li>
                  </Reveal>
                ))}
              </ul>
            </div>
            <ImageReveal
              src={ind.image}
              alt={ind.name}
              className="aspect-[4/5] border border-line lg:aspect-auto"
            />
          </div>
        </Container>
      </section>

      <section className="bg-ice py-24 md:py-28">
        <Container>
          <h2 className="font-heading text-2xl font-semibold tracking-tight text-navy md:text-3xl">
            Relevant solutions
          </h2>
          <div className="mt-10 grid grid-cols-1 gap-6 md:grid-cols-3">
            {SERVICES.map((s) => (
              <Link
                key={s.slug}
                to={`/solutions/${s.slug}`}
                className="group flex items-center justify-between border border-line bg-white p-7 transition-colors hover:border-electric"
              >
                <div>
                  <h3 className="font-heading text-lg font-medium text-navy">
                    {s.title}
                  </h3>
                  <p className="mt-1 text-sm text-slatey">{s.short}</p>
                </div>
                <ArrowUpRight className="ml-4 h-5 w-5 shrink-0 text-slatey transition-colors group-hover:text-electric" />
              </Link>
            ))}
          </div>
        </Container>
      </section>

      <CTABand />
    </>
  );
}
